import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useCallback, useState } from 'react';
import './Default.css';
import { useTranslation } from 'react-i18next';
import AppPageHeader from '../components/AppPageHeader';
import {
  clearPhoneNumber,
  strHasLower,
  strHasMinLength,
  strHasNumber,
  strHasSpecial,
  strHasUpper,
  strIsEmail,
  strIsPhone,
  strIsValidName,
} from '../utils/strings';
import RegisterPersonal from '../usecases/register/RegisterPersonal';
import RegisterSecurity from '../usecases/register/RegisterSecurity';
import { apiRegisterUser } from '../services/api';
import {
  AccountSecurityQuestion,
  SecurityQuestion,
} from '../services/types/apitypes';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import { returnToMain } from '../utils/nav';
import RegisterVerify from '../usecases/register/RegisterVerify';
import {
  cfgFirstNameMinLength,
  cfgLastNameMinLength,
  cfgPasswordMinLength,
  cfgSecurityAnswerMinLength,
  cfgSecurityQuestionLength,
} from '../utils/config';
import SecurityQuestions from '../usecases/shared/SecurityQuestions';
import { useHistory } from 'react-router';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const RegisterPage: React.FC = () => {
  const [t] = useTranslation('global');
  const [present] = useToaster();
  const router = useHistory();

  const [block, setBlock] = useState<string>('personal');

  const [password, setPassword] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const [securityQuestions, setSecurityQuestions] = useState<
    AccountSecurityQuestion[]
  >(Array(2));
  const [agree, setAgree] = useState<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [emailVerifyTime, setEmailVerifyTime] = useState<Date>(new Date());

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const getPersonalValidationError = () => {
    if (
      !strIsValidName(firstName && firstName.trim(), cfgFirstNameMinLength())
    ) {
      return t('register.errorfirstname');
    }
    if (!strIsValidName(lastName && lastName.trim(), cfgLastNameMinLength())) {
      return t('register.errorlastname');
    }
    if (!strIsEmail(email)) {
      return t('register.erroremail');
    }
    if (!password) {
      return t('register.errorpassword');
    }
    if (password != password2) {
      return t('register.errorpassword2');
    }
    if (
      !strHasMinLength(password, cfgPasswordMinLength()) ||
      !strHasLower(password) ||
      !strHasUpper(password) ||
      !strHasNumber(password) ||
      !strHasSpecial(password)
    ) {
      return t('register.errorpasswordquality');
    }
    if (!strIsPhone(phone)) {
      return t('register.errorphone');
    }
    return false;
  };

  const setClearPhone = (p: string) => {
     setPhone(clearPhoneNumber(p));
  }

  const getSecurityValidationError = () => {
    if (!agree) {
      return t('register.erroragree');
    }
    if (
      securityQuestions?.filter((value) => !!value)?.length <
      cfgSecurityQuestionLength()
    ) {
      return t('register.errorquestion');
    }
    if (
      securityQuestions?.some(
        (question) =>
          !strHasMinLength(question?.answer, cfgSecurityAnswerMinLength()),
      )
    ) {
      return t('register.erroranswer');
    }
    return false;
  };

  const continuePersonal = () => {
    const validationError = getPersonalValidationError();
    if (validationError === false) {
      setBlock('security');
    } else {
      present(getAppError(validationError));
    }
  };

  const continueSecurity = async () => {
    //window.postMessage('ionic-go-back');

    if (isSubmitting) {
      return;
    }
    const validationError = getSecurityValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiRegisterUser({
        email: email,
        allowNewsletter: true,
        firstName: firstName,
        lastName: lastName,
        password: password,
        phoneNumber: phone,
        securityQuestions,
      });
      present(getAppSuccess(t('register.success')));
      setEmailVerifyTime(new Date());
      setBlock('verify');
    } catch (error) {
      console.error('Error registering user ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('register.nosuccess')));
      }
      setBlock('security');
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const selectQuestion = useCallback(
    (s: SecurityQuestion) => {
      setSecurityQuestions((state) => {
        const newState = [...state];
        newState[currentQuestion] = {
          questionId: s.id,
          questionText: s.value,
          answer: '',
        };
        return newState;
      });
      setBlock('security');
    },
    [currentQuestion],
  );

  const setAnswers = useCallback((answer: string, position: number) => {
    setSecurityQuestions((state) => {
      const newState = [...state];
      newState[position] = {
        ...newState[position],
        answer: answer,
      };
      return newState;
    });
  }, []);

  const setQuestionsBlock = useCallback((question: number) => {
    setBlock('questions');
    setCurrentQuestion(question);
  }, []);

  return (
    <IonPage id="register-page">
      {block === 'personal' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => returnToMain(router)}
            title={t('register.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <RegisterPersonal
              isValid={getPersonalValidationError() === false}
              email={email}
              setEmail={setEmail}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              password={password}
              setPassword={setPassword}
              password2={password2}
              setPassword2={setPassword2}
              proceed={continuePersonal}
              phone={phone}
              setPhone={setClearPhone}
              setLastName={setLastName}
            />
          </IonContent>
        </>
      )}
      {block === 'security' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => {
              setBlock('personal');
            }}
            title={t('register.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <RegisterSecurity
              proceed={() => continueSecurity()}
              toQuestions={setQuestionsBlock}
              securityQuestions={securityQuestions}
              setAnswers={setAnswers}
              agree={agree}
              setAgree={setAgree}
              isValid={getSecurityValidationError() === false}
            />
          </IonContent>
        </>
      )}
      {block === 'questions' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('security')}
            title={t('register.questionstitle')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <SecurityQuestions
              selectQuestion={selectQuestion}
              excludedQuestions={securityQuestions
                ?.filter((q, index) => !!q && index !== currentQuestion)
                .map((q) => q?.questionId)}
            />
          </IonContent>
        </>
      )}
      {block === 'verify' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('security')}
            title={t('register.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <RegisterVerify
              time={emailVerifyTime}
              setTime={setEmailVerifyTime}
              email={email}
            ></RegisterVerify>
          </IonContent>
        </>
      )}
    </IonPage>
  );
};

export default RegisterPage;
